'use client';

import * as React from 'react';
import { styled } from '@mui/material/styles';
import { useStore } from '@base-ui/utils/store';
import { CalendarGrid } from '@mui/x-scheduler-internals/calendar-grid';
import { useEventCalendarStoreContext } from '@mui/x-scheduler-internals/use-event-calendar-store-context';
import { isWeekend } from '@mui/x-scheduler-internals/use-adapter';
import { useAdapterContext } from '@mui/x-scheduler-internals/use-adapter-context';
import { useEventOccurrencesWithTimelinePosition } from '@mui/x-scheduler-internals/use-event-occurrences-with-timeline-position';
import { eventCalendarOccurrencePlaceholderSelectors } from '@mui/x-scheduler-internals/event-calendar-selectors';
import { schedulerOtherSelectors } from '@mui/x-scheduler-internals/scheduler-selectors';
import { EventSkeleton } from "../event-skeleton/index.mjs";
import { EventDialogTrigger, useEventDialogContext } from "../event-dialog/EventDialog.mjs";
import { useEventCalendarStyledContext } from "../../../event-calendar/EventCalendarStyledContext.mjs";
import { getCellFocusBackground } from "../../utils/tokens.mjs";
import { useDayTimeGridInternalRenderers } from "./DayTimeGridInternalRenderersContext.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const DayTimeGridColumn = styled(CalendarGrid.TimeColumn, {
  name: 'MuiEventCalendar',
  slot: 'DayTimeGridColumn'
})(({
  theme
}) => ({
  borderInlineStart: `1px solid ${(theme.vars || theme).palette.divider}`,
  flexGrow: 1,
  flexShrink: 0,
  flexBasis: 0,
  minWidth: 0,
  position: 'relative',
  '&[data-weekend]': {
    backgroundColor: (theme.vars || theme).palette.action.hover
  },
  '&:focus-visible': {
    outline: 'none',
    backgroundColor: getCellFocusBackground(theme)
  }
}));
const DayTimeGridColumnInteractiveLayer = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'DayTimeGridColumnInteractiveLayer'
})({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0
});
const DayTimeGridCurrentTimeIndicator = styled(CalendarGrid.CurrentTimeIndicator, {
  name: 'MuiEventCalendar',
  slot: 'DayTimeGridCurrentTimeIndicator'
})(({
  theme
}) => ({
  position: 'absolute',
  zIndex: 2,
  top: 'var(--y-position)',
  left: 0,
  right: -1,
  height: 0,
  borderTop: `2px solid ${(theme.vars || theme).palette.primary.main}`
}));
const DayTimeGridCurrentTimeIndicatorCircle = styled('span', {
  name: 'MuiEventCalendar',
  slot: 'DayTimeGridCurrentTimeIndicatorCircle'
})(({
  theme
}) => ({
  position: 'absolute',
  zIndex: 1,
  left: -5,
  top: -5,
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: (theme.vars || theme).palette.primary.main
}));
export function TimeGridColumn(props) {
  const {
    day,
    showCurrentTimeIndicator,
    index,
    colIndex
  } = props;
  const adapter = useAdapterContext();
  const {
    classes
  } = useEventCalendarStyledContext();
  const start = React.useMemo(() => adapter.startOfDay(day.value), [adapter, day]);
  const end = React.useMemo(() => adapter.endOfDay(day.value), [adapter, day]);
  const {
    occurrences,
    maxIndex
  } = useEventOccurrencesWithTimelinePosition({
    occurrences: day.withoutPosition,
    maxSpan: Infinity
  });
  return /*#__PURE__*/_jsx(DayTimeGridColumn, {
    className: classes.dayTimeGridColumn,
    start: start,
    end: end,
    addPropertiesToDroppedEvent: addPropertiesToDroppedEvent,
    "aria-colindex": colIndex,
    "data-weekend": isWeekend(adapter, day.value) || undefined,
    style: {
      '--columns-count': maxIndex
    },
    children: /*#__PURE__*/_jsx(ColumnInteractiveLayer, {
      start: start,
      end: end,
      showCurrentTimeIndicator: showCurrentTimeIndicator,
      index: index,
      occurrences: occurrences,
      maxIndex: maxIndex
    })
  });
}
function ColumnInteractiveLayer({
  start,
  end,
  showCurrentTimeIndicator,
  index,
  occurrences,
  maxIndex
}) {
  // Context hooks
  const store = useEventCalendarStoreContext();
  const {
    onOpen: startEditing
  } = useEventDialogContext();
  const {
    classes
  } = useEventCalendarStyledContext();
  const {
    timeGridEvent: TimeGridEvent
  } = useDayTimeGridInternalRenderers();

  // Ref hooks
  const columnRef = React.useRef(null);

  // Selector hooks
  const isCreatingAnEvent = useStore(store, eventCalendarOccurrencePlaceholderSelectors.isCreatingInTimeRange, start, end);
  const placeholder = CalendarGrid.usePlaceholderInRange({
    start,
    end,
    occurrences,
    maxIndex
  });
  const isLoading = useStore(store, schedulerOtherSelectors.isLoading);
  React.useEffect(() => {
    if (!isCreatingAnEvent || !placeholder || !columnRef.current) {
      return;
    }
    startEditing(columnRef, placeholder);
  }, [isCreatingAnEvent, placeholder, startEditing]);
  return /*#__PURE__*/_jsxs(DayTimeGridColumnInteractiveLayer, {
    className: classes.dayTimeGridColumnInteractiveLayer,
    ref: columnRef,
    children: [isLoading && /*#__PURE__*/_jsx(EventSkeleton, {
      "data-variant": "time-column"
    }), !isLoading && occurrences.map(occurrence => /*#__PURE__*/_jsx(EventDialogTrigger, {
      occurrence: occurrence,
      children: /*#__PURE__*/_jsx(TimeGridEvent, {
        occurrence: occurrence,
        variant: "regular"
      })
    }, occurrence.key)), placeholder != null && /*#__PURE__*/_jsx(TimeGridEvent, {
      occurrence: placeholder,
      variant: "placeholder"
    }), showCurrentTimeIndicator ? /*#__PURE__*/_jsx(DayTimeGridCurrentTimeIndicator, {
      className: classes.dayTimeGridCurrentTimeIndicator,
      "aria-hidden": true,
      children: index === 0 && /*#__PURE__*/_jsx(DayTimeGridCurrentTimeIndicatorCircle, {
        className: classes.dayTimeGridCurrentTimeIndicatorCircle
      })
    }) : null]
  });
}
/**
 * Makes sure any event dropped in the time grid column is turned into a non all-day event.
 */
function addPropertiesToDroppedEvent() {
  return {
    allDay: false
  };
}